import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ExternalLink } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

// Overage to bill: closed months where a client used more block hours
// than they had, on clients billed through Stripe. A billing admin looks
// at the hours before anything is charged - time entries are sometimes
// corrected after a month ends (Kris, 2026-10-06) - then charges the
// month or waives it, with a reason.

interface OverageItem {
  periodId: string;
  organizationId: string;
  organizationName: string;
  month: string;
  allowanceHours: number;
  usedHours: number;
  overageHours: number;
  rateCents: number;
  amountCents: number;
  status: 'to_review' | 'pending' | 'open' | 'paid' | 'void' | 'uncollectible' | 'waived';
  hostedInvoiceUrl: string | null;
  lastError: string | null;
  waivedReason: string | null;
  paidAt: string | null;
}

const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const monthName = (month: string): string =>
  new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const STATUS: Record<OverageItem['status'], { label: string; cls: string }> = {
  to_review: { label: 'To review', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  pending: { label: 'Charging', cls: 'bg-[var(--color-surface)] text-[var(--color-muted)] border-[var(--color-border)]' },
  open: { label: 'Not paid', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  void: { label: 'Voided', cls: 'bg-[var(--color-surface)] text-[var(--color-muted)] border-[var(--color-border)]' },
  uncollectible: { label: 'Uncollectible', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
  waived: { label: 'Waived', cls: 'bg-[var(--color-surface)] text-[var(--color-muted)] border-[var(--color-border)]' },
};

export function OverageReviewPage(): React.ReactElement {
  useDocumentTitle('Overage to bill');
  const [items, setItems] = useState<OverageItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ items: OverageItem[] }>('/api/billing/overage');
      setItems(data.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load overage.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (item: OverageItem): void =>
    setItems((list) => (list ?? []).map((i) => (i.periodId === item.periodId ? item : i)));

  const toReview = (items ?? []).filter((i) => i.status === 'to_review' || i.status === 'open');
  const settled = (items ?? []).filter((i) => i.status !== 'to_review' && i.status !== 'open');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-header)]">Overage to bill</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Closed months where a client used more block hours than they had. Check the hours, then charge the
          month to the client&apos;s card or waive it. Only clients billed by card appear here.
        </p>
      </header>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
      {!items && !error && <p className="text-sm text-[var(--color-muted)]">Loading…</p>}

      {items && toReview.length === 0 && (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">Nothing to review. Every month with overage has been settled.</p>
        </Card>
      )}

      {toReview.map((item) => (
        <OverageRow key={item.periodId} item={item} onChange={replace} />
      ))}

      {settled.length > 0 && (
        <section>
          <h2 className="mb-2 text-base font-semibold text-[var(--color-header)]">Settled</h2>
          <div className="space-y-3">
            {settled.map((item) => (
              <OverageRow key={item.periodId} item={item} onChange={replace} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function OverageRow({ item, onChange }: { item: OverageItem; onChange: (i: OverageItem) => void }): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [reason, setReason] = useState('');

  async function act(action: 'charge' | 'waive' | 'refresh', json?: unknown): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch<{ item: OverageItem }>(`/api/billing/overage/${item.periodId}/${action}`, {
        method: 'POST',
        json: json ?? {},
      });
      onChange(data.item);
      setConfirming(false);
      setWaiving(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work. Nothing was charged.');
    } finally {
      setBusy(false);
    }
  }

  const status = STATUS[item.status];
  const actionable = item.status === 'to_review' || item.status === 'open';
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={`/organizations/${item.organizationId}`}
            className="text-base font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            {item.organizationName}
          </Link>
          <p className="text-sm text-[var(--color-muted)]">{monthName(item.month)}</p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${status.cls}`}>{status.label}</span>
      </div>

      <p className="mt-3 text-sm text-[var(--color-body)]">
        Used <strong>{item.usedHours}</strong> of {item.allowanceHours} hours ·{' '}
        <strong>{item.overageHours.toFixed(2)} hours over</strong> × {money(item.rateCents)} ={' '}
        <strong className="text-[var(--color-header)]">{money(item.amountCents)}</strong>
      </p>

      {item.waivedReason && <p className="mt-2 text-sm text-[var(--color-muted)]">Waived: {item.waivedReason}</p>}
      {(error ?? item.lastError) && actionable && (
        <p className="mt-2 rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {error ?? item.lastError}
        </p>
      )}

      {actionable && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {confirming ? (
            <>
              <span className="text-sm font-medium text-[var(--color-header)]">
                Charge {money(item.amountCents)} to {item.organizationName}&apos;s card now?
              </span>
              <Button onClick={() => void act('charge')} disabled={busy}>
                {busy ? 'Charging…' : 'Yes, charge it'}
              </Button>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
                Cancel
              </Button>
            </>
          ) : waiving ? (
            <>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why it is being waived"
                aria-label="Reason for waiving"
                className="block w-72 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
              <Button onClick={() => void act('waive', { reason })} disabled={busy || reason.trim().length < 3}>
                Waive it
              </Button>
              <Button variant="outline" onClick={() => setWaiving(false)} disabled={busy}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setConfirming(true)} disabled={busy}>
                {item.status === 'open' ? 'Retry the charge' : `Charge ${money(item.amountCents)}`}
              </Button>
              {item.status === 'to_review' && (
                <Button variant="outline" onClick={() => setWaiving(true)} disabled={busy}>
                  Waive
                </Button>
              )}
              {item.status === 'open' && (
                <Button variant="outline" onClick={() => void act('refresh')} disabled={busy}>
                  Check if the client paid
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {item.hostedInvoiceUrl && (
        <a
          href={item.hostedInvoiceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
        >
          The client&apos;s invoice <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      )}
    </Card>
  );
}
